import { type FC, useCallback, useMemo } from "react";
import notification from "@app/services/notification";
import { useScannerRegistration, type ScannerHintConfig, type ScannerRegistrationConfig } from "@app/scanner";
import type { ActOfCheckingAvailabilityContent } from "../../../../../../api/actOfCheckingAvailability";
import { resolveStockPartialInventoryLotScan } from "../../helpers/resolveStockPartialInventoryLotScan";
import type { UseStockPartialInventoryCardMutationsResult } from "../../useStockPartialInventoryCardMutations";

const PARTIAL_INVENTORY_SCAN_HINT: ScannerHintConfig = {
    stateKey: "stock-partial-inventory.card.scan-lot",
    title: "Сканируйте номер партии",
    description: "Найдём строку АПН по партии и откроем форму инвентаризации.",
    position: { placement: "bottom-left" },
    indicator: { variant: "scanner" },
};

/** Пропсы компонента {@link StockPartialInventoryScanPlaceholder}. */
interface StockPartialInventoryScanPlaceholderProps {
    /** Код текущего АПН. */
    actCode: number;
    /** Код склада АПН. */
    divisionCode: number;
    /** Строки состава текущего АПН. */
    contents: ActOfCheckingAvailabilityContent[];
    /** Включён ли сценарий сканирования партии. */
    enabled: boolean;
    /** Мутация добавления строки состава АПН. */
    insertContentMutation: UseStockPartialInventoryCardMutationsResult["insertContentMutation"];
    /**
     * Открывает форму инвентаризации строки АПН.
     * @param contentCode код найденной строки АПН
     */
    onOpenContent: (contentCode: number) => void;
}

/** Регистрирует сценарий сканирования партии в карточке частичной инвентаризации склада. */
export const StockPartialInventoryScanPlaceholder: FC<StockPartialInventoryScanPlaceholderProps> = ({
    actCode,
    divisionCode,
    contents,
    enabled,
    insertContentMutation,
    onOpenContent,
}) => {
    const handleScan = useCallback(
        async (barcode: string): Promise<void> => {
            const result = await resolveStockPartialInventoryLotScan({
                barcode,
                contents,
                divisionCode,
            });

            if (result.type === "notFound") {
                notification.warning(`Партия для скана "${barcode}" не найдена.`);
                return;
            }

            if (result.type === "contentNotFound") {
                if (!result.hasWarehouseRemainders) {
                    notification.warning("Партия не была найдена в АПН и не относится к складу АПН.");
                    return;
                }

                const inserted = await insertContentMutation.mutateAsync({
                    actCode,
                    parentLotCode: result.lot.LotCode,
                });

                notification.success(
                    "Партия не была найдена в АПН. Был создан элемент состава АПН для сканированной партии.",
                );
                onOpenContent(inserted.Code);
                return;
            }

            if (result.type === "ambiguous") {
                notification.warning("По партии найдено несколько строк АПН. Выберите строку вручную.");
                return;
            }

            onOpenContent(result.content.Code);
        },
        [actCode, contents, divisionCode, insertContentMutation, onOpenContent],
    );

    const scannerRegistration = useMemo(
        (): ScannerRegistrationConfig => ({
            enabled: enabled && !insertContentMutation.isPending,
            priority: "screen",
            captureInEditable: false,
            hint: PARTIAL_INVENTORY_SCAN_HINT,
            onScan: handleScan,
            errorSource: "stock.partialInventory.card.scanLot",
        }),
        [enabled, handleScan, insertContentMutation.isPending],
    );

    useScannerRegistration(scannerRegistration);

    return null;
};
